import { useState, useRef } from 'react';
import { callQueueImportApi } from '../../api/callQueueImport.js';

const COLUMN_REF = [
  { col: 'Client Name',           req: true,  note: 'Customer / shop name (used for lead matching)' },
  { col: 'Contact No 1',          req: false, note: 'Primary phone (10 digits)' },
  { col: 'Contact No 2',          req: false, note: 'Secondary phone' },
  { col: 'Last Call Date',        req: false, note: 'Stored as agent note on the call log' },
  { col: 'Last Call Status',      req: false, note: 'connected / not connected / not answer / switched off' },
  { col: 'Last Call Disposition', req: false, note: 'Callback date — Excel serial or DD/MM/YYYY' },
  { col: 'MATERIAL',              req: false, note: 'Informational — not imported' },
  { col: 'PRICE',                 req: false, note: 'Informational — not imported' },
  { col: 'Qty',                   req: false, note: 'Informational — not imported' },
  { col: 'Product Category',      req: false, note: 'Informational — not imported' },
  { col: 'LAST ORDER DATE',       req: false, note: 'Informational — not imported' },
];

const IMPORT_STEPS = [
  'Match each row to an existing lead (phone → exact name → fuzzy name)',
  'Create a CallLog entry with previous call status and callback date',
  'Remove existing queue entries for today for all matched leads',
  'Insert each lead into today\'s DailyCallQueue (follow-up, priority 2, round-robin across CRM users)',
  'Insert into DailyCallReport so the daily tracker reflects the updated queue immediately',
];

export default function CallQueueImportPage() {
  const [file, setFile]           = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const inputRef = useRef();

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { setError('Only .xlsx or .xls files are accepted.'); return; }
    setFile(f); setError(''); setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) { setError('Please select an Excel file first.'); return; }
    setUploading(true); setError(''); setResult(null); setProgress(0);
    try {
      const res = await callQueueImportApi.upload(file, setProgress);
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Import failed.');
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const reset = () => { setFile(null); setResult(null); setError(''); };

  return (
    <div className="page-body">

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.35rem' }}>Call Queue Import</h2>
        <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
          Upload a follow-up calls Excel (.xlsx). Leads are matched, previous calls are logged,
          today's queue is rebuilt, and the daily tracker is updated automatically.
        </p>
      </div>

      {/* Upload card */}
      {!result && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-body">
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => !uploading && inputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '36px 24px',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                background: dragging ? 'rgba(99,102,241,0.04)' : 'var(--bg)',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>📋</div>
              {file ? (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{file.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>
                    {(file.size / 1024).toFixed(1)} KB · click to change
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Drop your Excel file here</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>or click to browse (.xlsx, .xls)</div>
                </div>
              )}
              <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={e => pickFile(e.target.files?.[0])} />
            </div>

            {error && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 7, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '0.87rem' }}>
                {error}
              </div>
            )}

            {uploading && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 6, color: 'var(--text-muted)' }}>
                  <span>Importing data…</span><span>{progress}%</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: 'var(--primary)', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
                <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Large files may take 1–2 minutes. Please do not close this tab.
                </div>
              </div>
            )}

            <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!file || uploading}
                style={!file || uploading ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              >
                {uploading ? 'Importing…' : 'Start Import'}
              </button>
              {file && !uploading && (
                <button className="btn btn-outline" onClick={reset}>Clear</button>
              )}
              {!file && !uploading && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Select a file to continue</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Result card */}
      {result && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--primary)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: '1.5rem' }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Import Complete</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Call queue and daily tracker updated</div>
              </div>
              <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={reset}>Import Another</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Rows Processed',    value: result.summary.rows_processed,   color: '#6366f1' },
                { label: 'Leads Matched',      value: result.summary.leads_matched,    color: '#16a34a' },
                { label: 'Fuzzy Matched',      value: result.summary.leads_fuzzy,      color: '#d97706' },
                { label: 'Not Found',          value: result.summary.not_found,        color: '#ef4444' },
                { label: 'Contacts Added',     value: result.summary.contacts_added,   color: '#0891b2' },
                { label: 'Call Logs Created',  value: result.summary.logs_created,     color: '#7c3aed' },
                { label: 'Queue Inserted',     value: result.summary.queue_inserted,   color: '#059669' },
                { label: 'Tracker Updated',    value: result.summary.report_inserted,  color: '#059669' },
                { label: 'Skipped Rows',       value: result.summary.skipped_rows,     color: '#94a3b8' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg)', border: '1.5px solid var(--border)', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {result.fuzzy_matches?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, color: '#d97706' }}>
                  ⚠ Fuzzy-matched rows — please verify these:
                </div>
                <div style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: 7, padding: '10px 14px', fontSize: '0.82rem', maxHeight: 180, overflowY: 'auto' }}>
                  {result.fuzzy_matches.map((m, i) => <div key={i} style={{ padding: '2px 0' }}>{m}</div>)}
                </div>
              </div>
            )}

            {result.not_found?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, color: '#ef4444' }}>
                  Not found in database ({result.not_found.length}):
                </div>
                <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 7, padding: '10px 14px', fontSize: '0.82rem', maxHeight: 180, overflowY: 'auto' }}>
                  {result.not_found.map((n, i) => <div key={i} style={{ padding: '2px 0' }}>{n}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* What this does — info card */}
      <div className="card" style={{ marginBottom: 16, background: 'rgba(99,102,241,0.03)', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div className="card-body" style={{ padding: '14px 20px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, color: 'var(--primary)' }}>What this import does</div>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
            {IMPORT_STEPS.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      </div>

      {/* Column reference */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '0.97rem' }}>Expected Column Reference</h3>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Row 1 = header · partial name match (case-insensitive)</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Column Name</th>
                  <th style={{ textAlign: 'center' }}>Required</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {COLUMN_REF.map((c, i) => (
                  <tr key={c.col}>
                    <td className="text-muted">{i + 1}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.col}</td>
                    <td style={{ textAlign: 'center' }}>
                      {c.req
                        ? <span style={{ color: '#ef4444', fontWeight: 700 }}>Yes</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
